// API service for handling backend communication
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { 
  Submission, 
  CreateSubmissionRequest, 
  UpdateSubmissionRequest, 
  ApiResponse,
  GetUserSubmissionsResponse,
  Report,
  GenerateReportRequest,
  WritingTenet,
  StakeholderEmail,
  ErrorResponse
} from '../types';

// Environment-aware API base URL configuration
const getApiBaseUrl = (): string => {
  // Check for explicit API URL from environment
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For staging deployment, construct API URL from current domain
  const environment = process.env.REACT_APP_ENVIRONMENT;
  if (environment === 'staging') {
    // In staging, API is served from same domain under /api path
    const currentOrigin = window.location.origin;
    return `${currentOrigin}/api`;
  }
  
  // Default fallback
  return 'https://api.example.com';
};

const API_BASE_URL = getApiBaseUrl();

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

class ApiService {
  private tokenCache: { token: string; expiry: number } | null = null;
  private readonly retryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000
  };

  constructor() {
    console.log('ApiService initialized with base URL:', API_BASE_URL);
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    try {
      // Check if we have a cached token that's still valid
      if (this.tokenCache && Date.now() < this.tokenCache.expiry) {
        console.log('Using cached token');
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.tokenCache.token}`,
        };
      }

      console.log('Fetching fresh auth session...');
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        console.error('No authentication token available in session:', session);
        throw new Error('No authentication token available');
      }

      console.log('Got auth token, length:', token.length);
      
      // Cache the token with a 5-minute buffer before expiry
      const expiry = (session.tokens?.idToken?.payload.exp || 0) * 1000 - 300000;
      this.tokenCache = { token, expiry };
      
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      this.tokenCache = null;
      throw new Error('Authentication required');
    }
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    console.log('API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      // Handle authentication errors
      if (response.status === 401) {
        console.log('Got 401, signing out user');
        this.tokenCache = null;
        await signOut();
        const authError: ErrorResponse = {
          error: {
            code: 'AUTHENTICATION_ERROR',
            message: 'Authentication expired. Please log in again.',
          },
          timestamp: new Date().toISOString(),
          requestId: response.headers.get('x-request-id') || 'unknown',
        };
        return { error: authError };
      }

      // Handle other HTTP errors
      let errorData: ErrorResponse;
      try {
        const responseText = await response.text();
        console.log('Error response body:', responseText);
        errorData = JSON.parse(responseText);
      } catch {
        errorData = {
          error: {
            code: this.getErrorCodeFromStatus(response.status),
            message: this.getErrorMessageFromStatus(response.status),
          },
          timestamp: new Date().toISOString(),
          requestId: response.headers.get('x-request-id') || 'unknown',
        };
      }
      
      return { error: errorData };
    }

    const data = await response.json();
    console.log('Success response data:', data);
    return { data };
  }

  private getErrorCodeFromStatus(status: number): string {
    switch (status) {
      case 400: return 'BAD_REQUEST';
      case 403: return 'FORBIDDEN';
      case 404: return 'NOT_FOUND';
      case 409: return 'CONFLICT';
      case 422: return 'VALIDATION_ERROR';
      case 429: return 'RATE_LIMITED';
      case 500: return 'INTERNAL_SERVER_ERROR';
      case 502: return 'BAD_GATEWAY';
      case 503: return 'SERVICE_UNAVAILABLE';
      case 504: return 'GATEWAY_TIMEOUT';
      default: return 'UNKNOWN_ERROR';
    }
  }

  private getErrorMessageFromStatus(status: number): string {
    switch (status) {
      case 400: return 'Invalid request. Please check your input and try again.';
      case 403: return 'You do not have permission to perform this action.';
      case 404: return 'The requested resource was not found.';
      case 409: return 'This action conflicts with the current state. Please refresh and try again.';
      case 422: return 'The provided data is invalid. Please check your input.';
      case 429: return 'Too many requests. Please wait a moment and try again.';
      case 500: return 'An internal server error occurred. Please try again later.';
      case 502: return 'Service temporarily unavailable. Please try again later.';
      case 503: return 'Service is currently unavailable. Please try again later.';
      case 504: return 'Request timed out. Please try again.';
      default: return 'An unexpected error occurred. Please try again.';
    }
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async retryRequest<T>(
    requestFn: () => Promise<Response>,
    retryCount = 0
  ): Promise<ApiResponse<T>> {
    try {
      const response = await requestFn();
      
      // Don't retry client errors (4xx) except for 429 (rate limiting)
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        return this.handleResponse<T>(response);
      }

      // Retry server errors (5xx) and rate limiting (429)
      if ((response.status >= 500 || response.status === 429) && retryCount < this.retryConfig.maxRetries) {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(2, retryCount),
          this.retryConfig.maxDelay
        );
        
        console.warn(`Request failed with status ${response.status}, retrying in ${delay}ms (attempt ${retryCount + 1}/${this.retryConfig.maxRetries})`);
        await this.sleep(delay);
        return this.retryRequest<T>(requestFn, retryCount + 1);
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      // Retry network errors
      if (retryCount < this.retryConfig.maxRetries && error instanceof TypeError) {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(2, retryCount),
          this.retryConfig.maxDelay
        );
        
        console.warn(`Network error, retrying in ${delay}ms (attempt ${retryCount + 1}/${this.retryConfig.maxRetries}):`, error);
        await this.sleep(delay);
        return this.retryRequest<T>(requestFn, retryCount + 1);
      }

      // Return network error after max retries
      const networkError: ErrorResponse = {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Unable to connect to the server. Please check your internet connection and try again.',
        },
        timestamp: new Date().toISOString(),
        requestId: 'client-error',
      };
      
      return { error: networkError };
    }
  }

  async createSubmission(submission: CreateSubmissionRequest): Promise<ApiResponse<Submission>> {
    return this.retryRequest<Submission>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/submissions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(submission),
      });
    });
  }

  async getUserSubmissions(month: number, year: number): Promise<ApiResponse<GetUserSubmissionsResponse>> {
    return this.retryRequest<GetUserSubmissionsResponse>(async () => {
      const headers = await this.getAuthHeaders();
      // Zero-pad month to avoid CloudFront/API Gateway issues with single digits
      const paddedMonth = month.toString().padStart(2, '0');
      return fetch(`${API_BASE_URL}/api/submissions/by-date/${paddedMonth}/${year}`, {
        method: 'GET',
        headers,
      });
    });
  }

  async updateSubmission(id: string, submission: UpdateSubmissionRequest): Promise<ApiResponse<Submission>> {
    return this.retryRequest<Submission>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/submissions/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(submission),
      });
    });
  }

  async deleteSubmission(id: string): Promise<ApiResponse<void>> {
    return this.retryRequest<void>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/submissions/${id}`, {
        method: 'DELETE',
        headers,
      });
    });
  }

  // Joe's Evaluation Service
  async evaluateSubmission(request: {
    content: string;
    type: 'highlight' | 'lowlight';
    month: number;
    year: number;
  }): Promise<ApiResponse<{
    feedback: string;
    score: number;
    categories: {
      clarity: number;
      impact: number;
      specificity: number;
      professionalism: number;
    };
    suggestions: string[];
    strengths: string[];
    readyToSubmit: boolean;
    processingTimeMs: number;
  }>> {
    return this.retryRequest(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/submissions/evaluate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });
    });
  }

  // Admin Report Management
  async generateReport(request: GenerateReportRequest): Promise<ApiResponse<Report>> {
    return this.retryRequest<Report>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/reports/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });
    });
  }

  async getReport(month: number, year: number): Promise<ApiResponse<Report>> {
    return this.retryRequest<Report>(async () => {
      const headers = await this.getAuthHeaders();
      // Zero-pad month to avoid CloudFront/API Gateway issues with single digits
      const paddedMonth = month.toString().padStart(2, '0');
      // Add cache-busting timestamp to prevent stale data
      const timestamp = Date.now();
      return fetch(`${API_BASE_URL}/api/reports/by-date/${paddedMonth}/${year}?t=${timestamp}`, {
        method: 'GET',
        headers,
      });
    });
  }

  async approveReport(reportId: string): Promise<ApiResponse<Report>> {
    return this.retryRequest<Report>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/reports/${reportId}/approve`, {
        method: 'PUT',
        headers,
      });
    });
  }

  // Writing Tenets Management
  async getWritingTenets(): Promise<ApiResponse<WritingTenet[]>> {
    return this.retryRequest<WritingTenet[]>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/config/writing-tenets`, {
        method: 'GET',
        headers,
      });
    });
  }

  async updateWritingTenets(tenets: WritingTenet[]): Promise<ApiResponse<WritingTenet[]>> {
    return this.retryRequest<WritingTenet[]>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/config/writing-tenets`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ tenets }),
      });
    });
  }

  // Stakeholder Email Management
  async getStakeholderEmails(): Promise<ApiResponse<StakeholderEmail[]>> {
    return this.retryRequest<StakeholderEmail[]>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/config/stakeholder-emails`, {
        method: 'GET',
        headers,
      });
    });
  }

  async updateStakeholderEmails(emails: (StakeholderEmail | { email: string; action: 'delete' })[]): Promise<ApiResponse<StakeholderEmail[]>> {
    return this.retryRequest<StakeholderEmail[]>(async () => {
      const headers = await this.getAuthHeaders();
      return fetch(`${API_BASE_URL}/api/config/stakeholder-emails`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ emails }),
      });
    });
  }

  /**
   * Clear the cached authentication token
   * CRITICAL: Must be called during user logout to prevent cross-user data contamination
   */
  clearTokenCache(): void {
    console.log('Clearing ApiService token cache');
    this.tokenCache = null;
  }

  /**
   * Get the current API base URL for debugging
   */
  getBaseUrl(): string {
    return API_BASE_URL;
  }
}

export const apiService = new ApiService();